import {closeFormBySession, exitSubForm} from '../../Form/actions/form';
import {Dispatch, useCallback} from 'react';
import {ActionTypes, AdvancedDeliveryAction} from '../reducer/actions';
import {AdvancedDeliveryContextType} from '../reducer/model';
import {ItemType, LocationType, MapSettings, MarkerType, RouteType} from '../types';
import {loadRouteWarehouses, ResponseADP} from '../actions/action';
import {Form, formsStore} from '../../../stores/Form';
import {ADPFetchMax, DLVADDRESSSUM} from '../constants';
import {showInfoMessage} from '../../../_shared/Messages/actions/MessageActions';
import {texts} from '../texts';
import {startAndFetchSubform} from '../../Form/actions/batchedActions/standAloneFormBatch';
import {mappedColumnIds, prepareRowsToMarkersType} from '../utils';

export type ADPContextHandlers = {
    handleAdpOpen: (form?: ResponseADP) => void;
    closeADP: () => void;
    setSelectedMarker: (markers: MarkerType) => void;
    setRoutes: (routes: RouteType[], showAll?: boolean) => void;
    setIsCollapsableRouteOpen: (routeId: number, value: boolean) => void;
    setRouteLocations: (routeId: number, locations: LocationType[]) => void;
    setLocationItems: (routeId: number, locationId: number, items: ItemType[]) => void;
    setLocationItemForm: (routeId: number, locationId: number, form: Form) => void;
    setRouteLocationForm: (routeId: number, form: Form | undefined) => void;
    setRouteLocationFormLoading: (routeId: number, loading: boolean) => void;
    setSelectedLocations: (rows: number[]) => void;
    toggleRouteSelect: (route: RouteType, mapSettings: MapSettings) => void;
    unselectAllRoutes: () => void;
    setLocationSubformOpen: (routeId: number, locationSubformOpen: boolean) => void;
    setRouteColor: (routeId: number, color: string) => void;
    setRouteLocationReload: (routeId: number, isReload: boolean) => void;
    toggleDisplayedRoutes: (showAll: boolean) => void;
    showMaxRoutesCapReachedMessage: () => void;
    showMaxPlannedCapReachedMessage: () => void;
    setRouteLocationsAndForm: (
        routeId: number,
        locations: LocationType[],
        form: Form | undefined
    ) => void;
};

export const useAdvancedDeliveryContextAPI = (
    dispatch: Dispatch<AdvancedDeliveryAction>,
    state: AdvancedDeliveryContextType
) => {
    const setSelectedLocations = (rows: number[]) => {
        dispatch({type: ActionTypes.SET_SELECTED_LOCATIONS, payload: rows});
    };

    const toggleShowPlannedLocations = () => {
        dispatch({
            type: ActionTypes.SET_SHOW_PLANNED_LOCATIONS,
            payload: !state.isShowPlannedLocations
        });
    };

    const toggleShowRoute = useCallback(
        (routes: RouteType) => {
            const newRoutes = state.routes.map(r => {
                if (r.id === routes.id) {
                    return {...routes, show: !routes.isShow};
                }
                return r;
            });
            dispatch({type: ActionTypes.TOGGLE_SHOW_ROUTE, payload: newRoutes});
        },
        [state]
    );

    const toggleRouteSelect = useCallback(
        async (route: RouteType, mapSettings: MapSettings) => {
            const toggledRoute = state.routes.find(r => r.id === route.id);
            toggledRoute.isSelected = !toggledRoute.isSelected;
            if (toggledRoute.isSelected) {
                if (!route.locationSubform) {
                    const id = Number.parseInt(route.id);
                    const routesForm = state.routesForm;
                    routesForm.setCurrentRow(id);
                    await startAndFetchSubform(routesForm, DLVADDRESSSUM);
                    const form = routesForm.activeSubForm;
                    const locations: LocationType[] = form.records
                        .filter(x => !x.isNew)
                        .map(location => ({
                            id: location.recordIndex,
                            data: location,
                            itemsSubform: null
                        }));

                    await exitSubForm(form.session);
                    formsStore.getScreenBySession(routesForm.session).setActiveLevel(0);
                    toggledRoute.locationSubform = form;
                    toggledRoute.locations = locations;
                    toggledRoute.markers = prepareRowsToMarkersType(
                        locations.map(location => location.data),
                        mappedColumnIds(route.locationSubform.columns),
                        route
                    );
                }
                if (mapSettings.showStartEnd) {
                    await loadRouteWarehouses(route);
                }
            }
            dispatch({type: ActionTypes.TOGGLE_ROUTE_SELECT, payload: state.routes});
        },
        [state]
    );

    const unselectAllRoutes = useCallback(() => {
        state.routes.forEach(route => {
            route.isSelected = false;
        });
        dispatch({type: ActionTypes.UNSELECT_ALL_ROUTES, payload: state.routes});
    }, [state]);

    const toggleDisplayedRoutes = useCallback(
        (showAll: boolean) => {
            const routesToDisplay = state.routes.map(r => {
                return {...r, shouldDisplay: showAll || r.isSelected};
            });
            dispatch({
                type: ActionTypes.SET_DISPLAYED_ROUTES,
                payload: {routesToDisplay, showAll}
            });
        },
        [state]
    );

    const setRoutes = useCallback(
        (routes: RouteType[], showAll: boolean = state.showAll) => {
            dispatch({type: ActionTypes.SET_ROUTES, payload: {routes, showAll}});
        },
        [state]
    );

    const setIsCollapsableRouteOpen = useCallback((routeId: number, isCollapsableOpen: boolean) => {
        dispatch({
            type: ActionTypes.SET_ROUTE_COLLAPSABLE_OPEN,
            payload: {isCollapsableOpen, routeId}
        });
    }, []);

    const setRouteLocationsAndForm = useCallback(
        (routeId: number, locations: LocationType[], form: Form | undefined) => {
            dispatch({
                type: ActionTypes.SET_ROUTE_LOCATIONS_AND_FORM,
                payload: {routeId, locations, form}
            });
        },
        []
    );

    const setRouteLocations = useCallback((routeId: number, locations: LocationType[]) => {
        dispatch({type: ActionTypes.SET_ROUTE_LOCATIONS, payload: {routeId, locations}});
    }, []);

    const setRouteLocationReload = useCallback((routeId: number, value: boolean) => {
        dispatch({type: ActionTypes.SET_ROUTE_LOCATION_FORM_RELOAD, payload: {routeId, value}});
    }, []);

    const setRouteLocationForm = useCallback((routeId: number, form: Form | undefined) => {
        dispatch({type: ActionTypes.SET_ROUTE_LOCATION_FORM, payload: {routeId, form}});
    }, []);

    const setRouteLocationFormLoading = useCallback((routeId: number, loading: boolean) => {
        dispatch({type: ActionTypes.SET_ROUTE_LOCATION_FORM_LOADING, payload: {routeId, loading}});
    }, []);

    const setRouteColor = useCallback((routeId: number, color: string) => {
        dispatch({type: ActionTypes.SET_ROUTE_COLOR, payload: {routeId, color}});
    }, []);

    const setLocationSubformOpen = useCallback((routeId: number, locationSubformOpen: boolean) => {
        dispatch({
            type: ActionTypes.SET_LOCATION_SUBFORM_OPEN,
            payload: {routeId, locationSubformOpen}
        });
    }, []);

    const setLocationItems = useCallback(
        (routeId: number, locationId: number, items: ItemType[]) => {
            dispatch({type: ActionTypes.SET_LOCATION_ITEMS, payload: {routeId, locationId, items}});
        },
        []
    );

    const setLocationItemForm = useCallback((routeId: number, locationId: number, form: Form) => {
        dispatch({type: ActionTypes.SET_LOCATION_ITEM_FORM, payload: {routeId, locationId, form}});
    }, []);

    const setSelectedMarker = useCallback(
        (marker: MarkerType) => {
            const markers = state.markers.map(m => {
                if (m.id === marker.id) {
                    return {...marker, selected: !marker.selected};
                }
                return m;
            });
            dispatch({type: ActionTypes.SET_SELECTED_MARKERS, payload: markers});
        },
        [state]
    );

    const closeADP = async () => {
        const {planningForm, routesForm} = state;
        await closeFormBySession(planningForm?.session, false);
        await closeFormBySession(routesForm?.session, false);

        dispatch({type: ActionTypes.CLOSE_ADP});
    };
    const showMaxRoutesCapReachedMessage = () => {
        showInfoMessage({
            title: texts.routes_exceed_msg_title,
            content: texts.routes_exceed_msg_text
        });
    };

    const showMaxPlannedCapReachedMessage = () => {
        showInfoMessage({
            title: texts.exceed_msg_title,
            content: texts.exceed_msg_text
        });
    };

    const handleAdpOpen = useCallback(async (form: ResponseADP) => {
        const maxPlanningFormReached = form.planningForm?.records?.length > ADPFetchMax;
        const maxRoutesFormReached = form.routesForm?.records?.length > ADPFetchMax;

        if (maxPlanningFormReached && maxRoutesFormReached) {
            form.planningForm?.records.pop();
            form.routesForm?.records.pop();
            const showSecondModal = () => {
                setTimeout(() => {
                    showMaxRoutesCapReachedMessage();
                }, 1000);
            };
            showInfoMessage({
                title: texts.exceed_msg_title,
                content: texts.exceed_msg_text,
                onClose: showSecondModal
            });
        } else if (maxPlanningFormReached) {
            form.planningForm?.records.pop();
            showMaxPlannedCapReachedMessage();
        } else if (maxRoutesFormReached) {
            form.routesForm?.records.pop();
            showMaxRoutesCapReachedMessage();
        }
        dispatch({type: ActionTypes.SET_PLANING_FORM, payload: form.planningForm});
        dispatch({type: ActionTypes.SET_ROUTES_FORM, payload: form.routesForm});
    }, []);

    return {
        setSelectedLocations,
        toggleShowPlannedLocations,
        toggleShowRoute,
        handleAdpOpen,
        closeADP,
        setSelectedMarker,
        setRoutes,
        setLocationItems,
        setRouteLocations,
        setRouteLocationForm,
        setLocationItemForm,
        setRouteLocationFormLoading,
        setIsCollapsableRouteOpen,
        toggleRouteSelect,
        unselectAllRoutes,
        setLocationSubformOpen,
        setRouteColor,
        setRouteLocationReload,
        toggleDisplayedRoutes,
        showMaxRoutesCapReachedMessage,
        showMaxPlannedCapReachedMessage,
        setRouteLocationsAndForm
    } as ADPContextHandlers;
};
